import { getEmojiColorPalette } from "@/components/emoji/emoji-colors.ts";
import { Avatar, Box, Stack, Tag, Title } from "@skailer-com/uikit";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

export interface Props {
  title?: string;
  caption?: string;
  emoji?: string;
  avatar_url?: string;
  acronym?: string;
  contentSlot?: ReactNode;
  status?: string;
}

export const StickerCard = ({
  title,
  caption,
  emoji,
  contentSlot,
  status,
  avatar_url,
  acronym,
}: Props) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const emojiColors = useMemo(() => (emoji ? getEmojiColorPalette(emoji) : undefined), [emoji]);
  const gradientStyle = emojiColors
    ? {
        background: `radial-gradient(ellipse farthest-corner at 0% 100%, #${emojiColors[0]} 0%, #${emojiColors[1]} 100%)`,
      }
    : {
        background:
          "radial-gradient(ellipse farthest-corner at 0% 100%, hsl(var(--var-color-background-gradient-from)) 0%, hsl(var(--var-color-background-gradient-to)) 100%)",
      };

  useEffect(() => {
    const timer = setTimeout(() => {
      setHasAnimated(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    if (isHovering) return;

    if (cardRef.current) {
      const card = cardRef.current;
      const rect = card.getBoundingClientRect();
      const cardCenterX = rect.left + rect.width / 2;
      const cardCenterY = rect.top + rect.height / 2;

      const mouseX = mousePosition.x - cardCenterX;
      const mouseY = mousePosition.y - cardCenterY;

      const maxRotation = 5;
      const rotationFactor = 0.05;

      const yRotation = Math.min(Math.max(mouseX * rotationFactor, -maxRotation), maxRotation);
      const xRotation = Math.min(Math.max(-mouseY * rotationFactor, -maxRotation), maxRotation);

      setRotation({ x: xRotation, y: yRotation });
    }
  }, [mousePosition, isHovering]);

  const appliedRotation = isHovering ? { x: 0, y: 0 } : rotation;
  const transformStyle = hasAnimated
    ? {
        transform: `perspective(1000px) rotateX(${appliedRotation.x}deg) rotateY(${appliedRotation.y}deg)`,
        transition: "transform 0.3s ease-out",
      }
    : {
        animation: "cardAppear 750ms ease-out forwards",
      };

  return (
    <>
      <style>{`
        @keyframes cardAppear {
          0% {
            transform: perspective(1000px) rotateY(90deg) scale(0.3);
            opacity: 0;
          }
          60% {
            transform: perspective(1000px) rotateY(-12deg) scale(1.1);
            opacity: 1;
          }
          80% {
            transform: perspective(1000px) rotateY(4deg) scale(0.98);
          }
          100% {
            transform: perspective(1000px) rotateY(0deg) scale(1);
            opacity: 1;
          }
        }
      `}</style>
      <Stack
        ref={cardRef}
        className={"shadow-lg rounded-md p-6 w-[290px] min-h-[430px] shrink-0"}
        justify={"between"}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={transformStyle}
      >
        <Stack gap={"sm"}>
          <div
            className={
              "w-full h-[200px] rounded-xs bg-brand-dark/5 flex items-center justify-center relative"
            }
            style={gradientStyle}
          >
            {emoji && <Avatar emoji={emoji} size={"2xl"} variant={"plain"} />}
            {avatar_url && <Avatar url={avatar_url} size={"2xl"} variant={"plain"} />}
            {!emoji && !avatar_url && acronym && (
              <Avatar acronym={acronym} size={"2xl"} variant={"plain"} />
            )}
            {status && (
              <Box className={"absolute left-2 top-2"}>
                <Tag variant={"dark"} appearance={"filled"}>
                  {status}
                </Tag>
              </Box>
            )}
          </div>
          <Title header={title} caption={caption} />
        </Stack>
        <Stack>{contentSlot && contentSlot}</Stack>
      </Stack>
    </>
  );
};
